/**
 * Data table renderer — sortable tabular view of the same data.
 *
 * Supports:
 *  - Click column headers to sort
 *  - Click rows to drill down
 *  - Formatted metric values
 */
import { useState, useMemo } from 'react'
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react'
import type { PerfRow, MetricKey, GroupByDimension } from './types'
import { METRIC_LABELS, DIMENSION_LABELS, formatMetric, HIERARCHY_DIMENSIONS, getDimensionLabel } from './types'
import type { BenchmarkDataSeries } from './useBenchmarkQueries'

interface Props {
  data: PerfRow[]
  groupBy: GroupByDimension
  metrics: MetricKey[]
  seriesBy?: GroupByDimension
  onDrillDown?: (code: string, label: string) => void
  groupComparisons?: BenchmarkDataSeries[]
  primaryLabel?: string
}

const getLabel = getDimensionLabel

function getCode(row: PerfRow, dim: GroupByDimension): string {
  if (HIERARCHY_DIMENSIONS.has(dim) && row.entity_code) return String(row.entity_code)
  return String(row[dim] ?? '')
}

export default function TableRenderer({ data, groupBy, metrics, seriesBy, onDrillDown, groupComparisons, primaryLabel }: Props) {
  const [sortCol, setSortCol] = useState<string | null>(null)
  const [sortDesc, setSortDesc] = useState(true)

  const hasComparisons = groupComparisons && groupComparisons.some((c) => c.data.length > 0)

  // Build lookup maps for comparison data
  const compMaps = useMemo(() => {
    if (!hasComparisons) return []
    return groupComparisons!
      .filter((c) => c.data.length > 0)
      .map((c) => {
        const map = new Map<string, Record<string, number>>()
        for (const row of c.data) {
          const label = getLabel(row, groupBy)
          const vals: Record<string, number> = {}
          for (const m of metrics) {
            const v = row[m]
            vals[m] = typeof v === 'number' ? v : parseFloat(String(v)) || 0
          }
          map.set(label, vals)
        }
        return { source: c.source, map }
      })
  }, [groupComparisons, hasComparisons, groupBy, metrics])

  const columns = useMemo(() => {
    const cols: { key: string; label: string; isMetric: boolean }[] = [
      { key: '_label', label: DIMENSION_LABELS[groupBy], isMetric: false },
    ]
    if (seriesBy) {
      cols.push({ key: '_series', label: DIMENSION_LABELS[seriesBy], isMetric: false })
    }
    for (const m of metrics) {
      const label = hasComparisons ? `${primaryLabel || 'Primary'} ${METRIC_LABELS[m]}` : METRIC_LABELS[m]
      cols.push({ key: m, label, isMetric: true })
    }
    // Add comparison columns
    if (hasComparisons) {
      for (const comp of compMaps) {
        for (const m of metrics) {
          cols.push({
            key: `_comp_${comp.source.id}_${m}`,
            label: `${comp.source.label} ${METRIC_LABELS[m]}`,
            isMetric: true,
          })
        }
      }
    }
    return cols
  }, [groupBy, metrics, seriesBy, hasComparisons, compMaps, primaryLabel])

  const rows = useMemo(() => {
    const mapped = data.map((row) => {
      const r: Record<string, any> = {
        _label: getLabel(row, groupBy),
        _code: getCode(row, groupBy),
        _raw: row,
      }
      if (seriesBy) {
        r._series = getLabel(row, seriesBy)
      }
      for (const m of metrics) {
        r[m] = typeof row[m] === 'number' ? row[m] : parseFloat(String(row[m])) || 0
      }
      // Add comparison values
      for (const comp of compMaps) {
        const compVals = comp.map.get(r._label)
        for (const m of metrics) {
          r[`_comp_${comp.source.id}_${m}`] = compVals?.[m] ?? null
        }
      }
      return r
    })

    if (sortCol) {
      mapped.sort((a, b) => {
        const av = a[sortCol] ?? 0
        const bv = b[sortCol] ?? 0
        if (typeof av === 'string' && typeof bv === 'string') {
          return sortDesc ? bv.localeCompare(av) : av.localeCompare(bv)
        }
        return sortDesc ? (bv as number) - (av as number) : (av as number) - (bv as number)
      })
    }

    return mapped
  }, [data, groupBy, metrics, seriesBy, sortCol, sortDesc])

  const handleSort = (key: string) => {
    if (sortCol === key) {
      setSortDesc(!sortDesc)
    } else {
      setSortCol(key)
      setSortDesc(true)
    }
  }

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-gray-400">
        No data
      </div>
    )
  }

  return (
    <div className="overflow-auto h-full">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                onClick={() => handleSort(col.key)}
                className={`px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300 cursor-pointer select-none hover:bg-gray-100 dark:hover:bg-gray-700 whitespace-nowrap ${
                  col.isMetric ? 'text-right' : ''
                }`}
              >
                <span className="inline-flex items-center gap-1">
                  {col.label}
                  {sortCol === col.key ? (
                    sortDesc ? <ChevronDown size={12} /> : <ChevronUp size={12} />
                  ) : (
                    <ChevronsUpDown size={12} className="opacity-30" />
                  )}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              onClick={() => onDrillDown && row._code && onDrillDown(row._code, row._label)}
              className={`border-t border-gray-100 dark:border-gray-800 ${
                onDrillDown ? 'cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20' : ''
              }`}
            >
              {columns.map((col) => {
                const val = row[col.key]
                const isCompCol = col.key.startsWith('_comp_')
                // Extract the metric key from comparison column keys
                const metricForFormat = isCompCol
                  ? col.key.split('_').pop() as MetricKey
                  : col.key as MetricKey
                return (
                  <td
                    key={col.key}
                    className={`px-3 py-1.5 ${col.isMetric ? 'text-right tabular-nums' : ''} ${isCompCol ? 'text-gray-500' : ''}`}
                  >
                    {col.isMetric
                      ? (val === null || val === undefined ? '\u2014' : formatMetric(metricForFormat, val))
                      : val}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
